// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const categoryLabel: Record<string, string> = {
  registration: "Registration",
  payment: "Payment",
  invitation: "Invitations",
  operational: "Operational",
  authentication: "Authentication",
  admin_notification: "Admin Notifications",
  campaign: "Campaigns",
  utility: "Utility",
  roster: "Roster",
};

function fmtRelativeTime(iso: string | null | undefined) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel }: any) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="bg-surface-card border border-stroke-subtle rounded-token-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-heading-3 text-ink-primary mb-2">{title}</h3>
        <p className="text-body-md text-ink-secondary mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-surface-page border border-stroke-subtle hover:border-stroke-strong px-4 py-2 rounded-token-md text-body-sm text-ink-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-status-danger hover:bg-status-danger/90 text-white px-4 py-2 rounded-token-md text-body-sm font-medium transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplateRow({ template }: any) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleEdit = () => navigate(`/admin/messages/templates/${template.id}`);

  const handleDuplicate = async () => {
    const newKey = `${template.key}_copy_${Date.now().toString(36)}`;
    const { data: newTpl, error } = await (supabase as any)
      .from("message_templates")
      .insert({
        organization_id: template.organization_id,
        key: newKey,
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        trigger_type: template.trigger_type,
        is_active: false,
      })
      .select()
      .single();

    if (error) {
      toast.error("Duplicate failed: " + error.message);
      return;
    }

    const channelsToCopy = (template.channels || []).map((c: any) => ({
      template_id: newTpl.id,
      channel: c.channel,
      is_enabled: false,
      subject: c.subject,
      body_html: c.body_html,
      body_text: c.body_text,
      sms_body: c.sms_body,
      push_title: c.push_title,
      push_body: c.push_body,
    }));
    if (channelsToCopy.length) {
      await (supabase as any).from("message_template_channels").insert(channelsToCopy);
    }

    queryClient.invalidateQueries({ queryKey: ["message-templates"] });
    toast.success(`Duplicated as "${newTpl.name}"`);
    navigate(`/admin/messages/templates/${newTpl.id}`);
  };

  const handleDelete = async () => {
    const { error } = await (supabase as any)
      .from("message_templates")
      .delete()
      .eq("id", template.id);
    if (error) {
      toast.error("Delete failed");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["message-templates"] });
    toast.success("Template deleted");
    setConfirmDelete(false);
  };

  const lastModified = (template.channels || []).reduce(
    (latest: string | null, c: any) =>
      !latest || (c.last_modified_at && c.last_modified_at > latest) ? c.last_modified_at : latest,
    null,
  );

  return (
    <div className="px-4 py-3 border-b border-stroke-subtle last:border-b-0 flex items-center gap-4 hover:bg-surface-sunken/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-body-md text-ink-primary font-medium">{template.name}</span>
          {!template.is_active && (
            <span className="text-caption bg-status-danger-subtle text-status-danger px-2 py-0.5 rounded-token-sm uppercase tracking-wide">
              Inactive
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-caption text-ink-tertiary mt-0.5 truncate">{template.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {(template.channels || []).map((c: any) => {
            const incomplete =
              c.is_enabled &&
              c.channel === "email" &&
              ((!c.subject || !c.subject.trim()) || (!c.body_html || !c.body_html.trim()));
            return (
              <span key={c.channel} className="text-caption text-ink-tertiary inline-flex items-center gap-1">
                {c.channel === "email" ? "📧" : c.channel === "sms" ? "💬" : "🔔"} {c.channel}
                {!c.is_enabled && <span className="text-status-warning ml-1">·off</span>}
                {incomplete && (
                  <span
                    className="bg-status-warning-subtle text-status-warning border border-status-warning/30 rounded-token-sm px-1.5 py-0.5 text-[10px] font-medium"
                    title="Missing subject or body — won't send"
                  >
                    ⚠ Incomplete
                  </span>
                )}
              </span>
            );
          })}
          <span className="text-caption text-ink-tertiary">·</span>
          <span className="text-caption text-ink-tertiary capitalize">{template.trigger_type}</span>
          <span className="text-caption text-ink-tertiary">·</span>
          {(template.trigger_events || []).length > 0 ? (
            <span
              className="bg-cyan-subtle text-cyan border border-cyan/30 rounded-token-sm px-1.5 py-0.5 text-[10px] font-medium"
              title={(template.trigger_events || []).map((t: any) => t.description).join("\n")}
            >
              🤖 Auto: {template.trigger_events.length} trigger
              {template.trigger_events.length === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="bg-surface-sunken text-ink-tertiary border border-stroke-subtle rounded-token-sm px-1.5 py-0.5 text-[10px] font-medium">
              Manual only
            </span>
          )}
          {lastModified && (
            <>
              <span className="text-caption text-ink-tertiary">·</span>
              <span className="text-caption text-ink-tertiary">
                Modified {fmtRelativeTime(lastModified)}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={handleEdit}
          className="bg-surface-page border border-stroke-subtle hover:border-cyan text-ink-primary hover:text-cyan px-3 py-1.5 rounded-token-md text-body-sm transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={handleDuplicate}
          className="bg-surface-page border border-stroke-subtle hover:border-cyan text-ink-primary hover:text-cyan px-3 py-1.5 rounded-token-md text-body-sm transition-colors"
          title="Duplicate this template"
        >
          Duplicate
        </button>
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="text-status-danger hover:bg-status-danger-subtle px-3 py-1.5 rounded-token-md text-body-sm transition-colors"
          title="Delete this template"
        >
          Delete
        </button>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={`Delete "${template.name}"?`}
          message="This template will be permanently removed. Any system emails or SMS that reference this template by key will fail until the template is recreated. This cannot be undone."
          confirmLabel="Delete Template"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

export default function MessageTemplatesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    document.title = "Communication Center | RLevents";
  }, []);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["message-templates"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("message_templates")
        .select(`
          id, key, name, description, category, trigger_type, is_active, organization_id, trigger_events,
          channels:message_template_channels(
            id, channel, is_enabled, subject, body_html, body_text,
            sms_body, push_title, push_body, version, last_modified_at
          )
        `)
        .order("category", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const distinctCategories = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t: any) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t: any) => {
      if (q) {
        const hay = `${t.name} ${t.key} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (channelFilter !== "all") {
        const has = (t.channels || []).some((c: any) => c.channel === channelFilter);
        if (!has) return false;
      }
      if (statusFilter === "active" && !t.is_active) return false;
      if (statusFilter === "inactive" && t.is_active) return false;
      return true;
    });
  }, [templates, search, categoryFilter, channelFilter, statusFilter]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    for (const t of filtered) {
      const c = t.category || "other";
      g[c] = g[c] || [];
      g[c].push(t);
    }
    return g;
  }, [filtered]);

  return (
    <div className="px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-body-sm text-ink-tertiary mb-1">Communication Center › Templates</p>
          <h1 className="text-display-sm text-ink-primary font-display">Communication Center</h1>
          <p className="text-body-md text-ink-secondary mt-2 max-w-2xl">
            Edit the content of every system email and SMS sent by RLevents. Templates are
            organization-wide — changes apply to all events.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/admin/messages/templates/new")}
          className="bg-cyan text-cyan-foreground hover:bg-cyan-hover px-5 py-2.5 rounded-token-md font-medium transition-colors whitespace-nowrap"
        >
          + New Template
        </button>
      </div>

      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search by name, key, description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary md:col-span-1"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
        >
          <option value="all">All categories</option>
          {distinctCategories.map((c) => (
            <option key={c} value={c}>
              {categoryLabel[c] || c}
            </option>
          ))}
        </select>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
        >
          <option value="all">All channels</option>
          <option value="email">Has Email</option>
          <option value="sms">Has SMS</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-surface-page border border-stroke-subtle rounded-token-md px-3 py-2 text-body-sm text-ink-primary"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {isLoading ? (
        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-ink-tertiary">
          Loading templates...
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl p-12 text-center text-body-md text-ink-tertiary">
          No templates match the current filters.
        </div>
      ) : (
        <div className="bg-surface-card border border-stroke-subtle rounded-token-xl overflow-hidden">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="bg-surface-sunken px-4 py-2 border-b border-stroke-subtle">
                <span className="text-caption text-ink-tertiary uppercase tracking-wide font-medium">
                  {categoryLabel[cat] || cat}
                </span>
                <span className="text-caption text-ink-tertiary ml-2">({items.length})</span>
              </div>
              {items.map((t: any) => (
                <TemplateRow key={t.id} template={t} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
